import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { Creative, CreativeType } from '../../../../types/creative';
import { createCreative, updateCreative } from '../../../../api/creativeApi';
import { Campaign } from '../../../../types/campaign';
import { getCampaigns } from '../../../../api/campaignApi';
import Select, { ActionMeta, SingleValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { createOption, Option } from '../../../../helpers/selectHelpers';
import selectSizeOptions from '../../../../constants/selectSizeOptions';
import { Loader2 } from 'lucide-react';

interface AddEditCreativeModalProps {
    creative: Creative|null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Creative) => void;
    onUpdateData: (newData: Creative) => void;
}

const AddEditCreativeModal = ({ creative, isOpen, toggle, onAddData, onUpdateData }: AddEditCreativeModalProps) => {
     const [loading, setLoading] = useState<boolean>(false);
     const [campaigns, setCampaigns] = useState<Campaign[]>([]);
     const [selectCampaignValue, setSelectCampaignValue] = useState<SingleValue<Option>>();
     const [selectSizeValue, setSelectSizeValue] = useState<SingleValue<Option>>();
    // local mutable options for creatable sizes
     const [sizeOptions, setSizeOptions] = useState(selectSizeOptions);

    // Função utilitária para obter extensões permitidas conforme o tipo
    const getAcceptedExtensions = (type: string) => {
        switch (type) {
            case CreativeType.AUDIO_VAST:
                return ['.aac', '.mp3'];
            case CreativeType.HTML5:
                return ['.zip'];
            case CreativeType.VIDEO_VAST:
                return ['.mp4'];
            case CreativeType.IMAGE:
                return ['.gif', '.jpg', '.jpeg', '.png'];
            default:
                return [];
        }
    };

    // Função utilitária para validar extensão do arquivo
    const isValidExtension = (file: File, type: string) => {
        const allowed = getAcceptedExtensions(type);
        if (!file) return false;
        const fileName = file.name.toLowerCase();
        return allowed.some(ext => fileName.endsWith(ext));
    };

    const allowedCompanionAdFileExtensions = [
        ...getAcceptedExtensions(CreativeType.HTML5),
        ...getAcceptedExtensions(CreativeType.IMAGE),
    ];

    useEffect(() => {
        getCampaigns().then(setCampaigns);
    }, []);

    const toggleAddModal = () => {
        toggle();
    };

    const selectCampaignOnChange = (newValue: SingleValue<Option>, actionMeta: ActionMeta<Option>) => {
        setSelectCampaignValue(newValue);
        handleForm.setFieldValue('campaignId', newValue ? newValue.value : null);
    };

    const selectSizeOnChange = (newValue: SingleValue<Option>, actionMeta: ActionMeta<Option>) => {
        setSelectSizeValue(newValue);
        handleForm.setFieldValue('size', newValue ? newValue.value : null);
    };

    // allow creating custom size options (must be `${width}x${height}` or 'fluid')
    const createSizeOption = (inputValue: string) => {
        const isValid = inputValue === 'fluid' || /^\d+x\d+$/.test(inputValue);
        if (!isValid) {
            toast.error('Tamanho inválido. Use o formato {largura}x{altura}, ex: 300x250');
            return;
        }
        const newOpt = createOption(inputValue, inputValue);
        setSizeOptions(prev => {
            if (prev.find(o => o.value === inputValue)) return prev;
            return [...prev, newOpt];
        });
        setSelectSizeValue(newOpt);
        handleForm.setFieldValue('size', inputValue);
    };

    const handleForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            id: creative ? creative.id : '',
            name: creative ? creative.name : '',
            type: creative ? creative.type : '',
            size: creative ? creative.size : '',
            campaignId: '',
            creativeFile: undefined,
            creativeCompanionAdFile: undefined,
        },
        validationSchema: Yup.object({
            name: Yup.string().trim().required('O nome é obrigatório'),
            type: Yup.string().required('Campo obrigatório'),
            size: Yup.string().required('Campo obrigatório').test('is-valid-size', 'Tamanho inválido', (value) => {
                if (!value) return false;
                if (value === 'fluid') return true;
                return /^\d+x\d+$/.test(value);
            }),
            campaignId: Yup.number().integer(),
            creativeFile: Yup.mixed()
                .required("É necessário enviar um arquivo")
                .test(
                    'fileFormat',
                    'Arquivo inválido para o tipo selecionado',
                    function (value) {
                        const { type } = this.parent;
                        if (!value || !type) return false;
                        // Verifica se value é um File antes de acessar 'name'
                        if (!(value instanceof File)) return false;
                        return isValidExtension(value, type);
                    }
                ),
            creativeCompanionAdFile: Yup.mixed()
                .test(
                    'fileFormat',
                    'Arquivo inválido para o tipo selecionado',
                    function (value) {
                        const { type } = this.parent;
                        if (!value || !type) return true; // allow undefined or missing field
                        if (!(value instanceof File)) return false;
                        return allowedCompanionAdFileExtensions.some(ext => value.name.toLowerCase().endsWith(ext));
                    }
                ),
        }),
        onSubmit: (values: any) => {
            setLoading(true);
            if (creative) {
                updateCreative(values)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Criativo atualizado com sucesso!');
                        onUpdateData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                const formData = new FormData();
                formData.append('name', values.name);
                formData.append('type', values.type);
                if (values.campaignId) {
                    formData.append('campaignId', values.campaignId);
                }
                formData.append('size', values.size);
                const creativeFile: HTMLInputElement | null = document.querySelector('#creative-file');
                if (values.creativeFile && creativeFile && creativeFile.files) {
                    formData.append('creativeFile', creativeFile.files[0]);
                }
                const creativeCompanionAdFile: HTMLInputElement | null = document.querySelector('#creative-companion-ad-file');
                if (values.creativeCompanionAdFile && creativeCompanionAdFile && creativeCompanionAdFile.files) {
                    formData.append('creativeCompanionAdFile', creativeCompanionAdFile.files[0]);
                }
                createCreative(formData)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Criativo cadastrado com sucesso!');
                        onAddData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    // When editing an existing creative, ensure the select value exists in local options
    useEffect(() => {
        if (creative && creative.size) {
            const sizeVal = creative.size;
            const existing = sizeOptions.find(o => o.value === sizeVal);
            if (existing) {
                setSelectSizeValue(existing);
            } else {
                const newOpt = createOption(sizeVal, sizeVal);
                setSizeOptions(prev => (prev.find(o => o.value === sizeVal) ? prev : [...prev, newOpt]));
                setSelectSizeValue(newOpt);
            }
            handleForm.setFieldValue('size', sizeVal);
        } else {
            setSelectSizeValue(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creative]);

    // Determina as extensões aceitas dinamicamente
    const acceptedCreativeFile = getAcceptedExtensions(handleForm.values.type).join(', ');
    const acceptedCompanionAdFile = allowedCompanionAdFileExtensions.join(', ');

    return (
        <Dialog open={isOpen} onOpenChange={toggleAddModal}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{creative ? 'Editar' : 'Adicionar'} criativo</DialogTitle>
                    <DialogDescription>
                        Preencha os dados do criativo.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleForm.handleSubmit();
                    return false;
                }}>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 gap-4">
                            <div className="flex items-center gap-2">
                                <input
                                    name="type"
                                    type="radio"
                                    id="type_image"
                                    value={CreativeType.IMAGE}
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    checked={handleForm.values.type === CreativeType.IMAGE}
                                    className="w-4 h-4"
                                />
                                <Label htmlFor="type_image">Imagem</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    name="type"
                                    type="radio"
                                    id="type_video_vast"
                                    value={CreativeType.VIDEO_VAST}
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    checked={handleForm.values.type === CreativeType.VIDEO_VAST}
                                    className="w-4 h-4"
                                />
                                <Label htmlFor="type_video_vast">Vídeo Vast</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    name="type"
                                    type="radio"
                                    id="type_audio_vast"
                                    value={CreativeType.AUDIO_VAST}
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    checked={handleForm.values.type === CreativeType.AUDIO_VAST}
                                    className="w-4 h-4"
                                />
                                <Label htmlFor="type_audio_vast">Audio Vast</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    name="type"
                                    type="radio"
                                    id="type_html5"
                                    value={CreativeType.HTML5}
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    checked={handleForm.values.type === CreativeType.HTML5}
                                    className="w-4 h-4"
                                />
                                <Label htmlFor="type_html5">HTML5</Label>
                            </div>
                        </div>
                        {handleForm.touched.type && handleForm.errors.type && (
                            <p className="text-sm text-red-500">{handleForm.errors.type}</p>
                        )}

                        <div className="grid gap-2">
                            <Label htmlFor="name">Nome</Label>
                            <Input
                                type="text"
                                id="name"
                                name="name"
                                onChange={handleForm.handleChange}
                                onBlur={handleForm.handleBlur}
                                value={handleForm.values.name}
                            />
                            {handleForm.touched.name && handleForm.errors.name && (
                                <p className="text-sm text-red-500">{handleForm.errors.name}</p>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="campaignId">Campanha</Label>
                            <Select
                                className="basic-single"
                                classNamePrefix="select"
                                isClearable={true}
                                isSearchable={true}
                                id="campaignId"
                                name="campaignId"
                                options={campaigns.map((campaign) => createOption(campaign.name, String(campaign.id)))}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        borderColor: !!(handleForm.touched.campaignId && handleForm.errors.campaignId) ? 'red' : '#ced4da',
                                    }),
                                }}
                                onChange={selectCampaignOnChange}
                                value={selectCampaignValue}
                            />
                            {handleForm.touched.campaignId && handleForm.errors.campaignId && (
                                <p className="text-sm text-red-500">{handleForm.errors.campaignId}</p>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="size">Tamanho</Label>
                            <CreatableSelect
                                className="basic-single"
                                classNamePrefix="select"
                                isClearable={true}
                                isSearchable={true}
                                id="size"
                                name="size"
                                options={sizeOptions}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        borderColor: !!(handleForm.touched.size && handleForm.errors.size) ? 'red' : '#ced4da',
                                    }),
                                }}
                                onChange={selectSizeOnChange}
                                onCreateOption={createSizeOption}
                                value={selectSizeValue}
                            />
                            {handleForm.touched.size && handleForm.errors.size && (
                                <p className="text-sm text-red-500">{handleForm.errors.size}</p>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="creative-file">Criativo <sup>Extensões aceitas: {acceptedCreativeFile}</sup></Label>
                            <Input
                                type="file"
                                id="creative-file"
                                name="creativeFile"
                                accept={acceptedCreativeFile}
                                onChange={(e) => {
                                    handleForm.setFieldValue('creativeFile', e.currentTarget.files && e.currentTarget.files[0]);
                                }}
                                onBlur={handleForm.handleBlur}
                            />
                            {handleForm.touched.creativeFile && handleForm.errors.creativeFile && (
                                <p className="text-sm text-red-500">{handleForm.errors.creativeFile}</p>
                            )}
                        </div>

                        {(handleForm.values.type === CreativeType.VIDEO_VAST || handleForm.values.type === CreativeType.AUDIO_VAST) && (
                            <div className="grid gap-2">
                                <Label htmlFor="creative-companion-ad-file">Criativo - Companion Ad <sup>Extensões aceitas: {acceptedCompanionAdFile}</sup></Label>
                                <Input
                                    type="file"
                                    id="creative-companion-ad-file"
                                    name="creativeCompanionAdFile"
                                    accept=".jpeg,.jpg,.png,.mp4,.zip"
                                    onChange={(e) => {
                                        handleForm.setFieldValue('creativeCompanionAdFile', e.currentTarget.files && e.currentTarget.files[0]);
                                    }}
                                    onBlur={handleForm.handleBlur}
                                />
                                {handleForm.touched.creativeCompanionAdFile && handleForm.errors.creativeCompanionAdFile && (
                                    <p className="text-sm text-red-500">{handleForm.errors.creativeCompanionAdFile}</p>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="submit" disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {creative ? 'Editar' : 'Cadastrar'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddEditCreativeModal;
